/**
 * Tiny fetch wrapper. Every call sends the session cookie and turns a
 * non-2xx response into an Error carrying the server's message, so
 * screens can just `catch (err) { setError(err.message) }`.
 */
async function request(method, path, body) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Cannot reach the CyberCub server. Is it running?');
  }

  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const data = isJson ? await res.json().catch(() => ({})) : {};

  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data.code;
    err.issues = data.issues;
    throw err;
  }
  return data;
}

const qs = (params = {}) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '' && v !== 'all') search.set(k, v);
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  del: (path) => request('DELETE', path),

  // --- auth ---
  me: () => request('GET', '/auth/me'),
  login: (userId, password) => request('POST', '/auth/login', { userId, password }),
  logout: () => request('POST', '/auth/logout'),

  // --- two-step verification (superadmins only) ---
  // login() resolves to { mfaRequired: true } instead of a user when the
  // account has it on; finish with totpVerify.
  totpVerify: (body) => request('POST', '/auth/totp/verify', body),
  totpStatus: () => request('GET', '/auth/totp/status'),
  totpSetup: () => request('POST', '/auth/totp/setup'),
  totpEnable: (code) => request('POST', '/auth/totp/enable', { code }),
  totpDisable: (password) => request('POST', '/auth/totp/disable', { password }),
  changePassword: (currentPassword, newPassword) =>
    request('POST', '/auth/change-password', { currentPassword, newPassword }),

  // --- learning ---
  tracks: () => request('GET', '/learn/tracks'),
  lesson: (slug) => request('GET', `/learn/lessons/${slug}`),
  saveProgress: (slug, lastPage, secondsSpent) =>
    request('POST', `/learn/lessons/${slug}/progress`, { lastPage, secondsSpent }),
  submitQuiz: (slug, answers, secondsSpent) =>
    request('POST', `/learn/lessons/${slug}/quiz`, { answers, secondsSpent }),
  studentDashboard: () => request('GET', '/learn/dashboard'),
  saveProject: (slug, body) => request('POST', `/learn/lessons/${slug}/project`, body),
  loadProject: (slug) => request('GET', `/learn/lessons/${slug}/project`),

  // --- payments ---
  paymentStatus: (course) =>
    request('GET', `/payments/status${course ? `?course=${encodeURIComponent(course)}` : ''}`),
  createOrder: (course) => request('POST', '/payments/create-order', course ? { course } : {}),
  verifyPayment: (body) => request('POST', '/payments/verify', body),
  paymentFailed: (body) => request('POST', '/payments/failed', body),

  // --- payment review (superadmin) ---
  adminPayments: () => request('GET', '/superadmin/payments'),
  approvePayment: (id) => request('POST', `/superadmin/payments/${id}/approve`),
  rejectPayment: (id, body) => request('POST', `/superadmin/payments/${id}/reject`, body),

  // --- admin ---
  adminSchools: () => request('GET', '/admin/schools'),
  adminOverview: (schoolId) => request('GET', `/admin/overview${qs({ schoolId })}`),
  adminStudents: (params) => request('GET', `/admin/students${qs(params)}`),
  adminStudent: (id, schoolId) => request('GET', `/admin/students/${id}${qs({ schoolId })}`),

  // --- superadmin ---
  superOverview: () => request('GET', '/superadmin/overview'),
  superSchools: () => request('GET', '/superadmin/schools'),
  createSchool: (body) => request('POST', '/superadmin/schools', body),
  updateSchool: (id, body) => request('PATCH', `/superadmin/schools/${id}`, body),
  superUsers: (params) => request('GET', `/superadmin/users${qs(params)}`),
  createUser: (body) => request('POST', '/superadmin/users', body),
  bulkCreateUsers: (body) => request('POST', '/superadmin/users/bulk', body),
  // Ids repeat across the three account tables, so every :id call
  // carries the role too.
  updateUser: (role, id, body) => request('PATCH', `/superadmin/users/${id}?role=${role}`, body),
  resetUserPassword: (role, id, body) =>
    request('POST', `/superadmin/users/${id}/reset-password?role=${role}`, body),
  unlockUser: (role, id) => request('POST', `/superadmin/users/${id}/unlock?role=${role}`),
  deleteUser: (role, id) => request('DELETE', `/superadmin/users/${id}?role=${role}`),
  auditLog: () => request('GET', '/superadmin/audit'),
  contactMessages: (params) => request('GET', `/superadmin/messages${qs(params)}`),
  setMessageStatus: (id, status) => request('PATCH', `/superadmin/messages/${id}`, { status }),
  deleteMessage: (id) => request('DELETE', `/superadmin/messages/${id}`),

  // --- contact (public) ---
  sendContactMessage: (body) => request('POST', '/public/contact', body),
};
